// frontend/src/pages/admin/UserAccessPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Spin } from 'antd';
import { useAuth } from '../../context/AuthContext';
import './AdminPanelTabs.css';

// Import the actual components for each tab
import User from './User';
import AccountApprovals from './AccountApprovals';
import Organizations from './Organization';
import Roles from './Role';

const { TabPane } = Tabs;

const UserAccessPage = () => {
  const { 
    user, 
    loading, 
    hasPermission,
    PERMISSION_LEVELS,
    initialized 
  } = useAuth();
  const [activeTabKey, setActiveTabKey] = useState('user-directory');

  // Define tabs with their permission requirements
  const tabs = useMemo(() => [
    {
      key: 'user-directory',
      tab: 'User Directory',
      content: <User />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'account-approvals',
      tab: 'Account Approvals',
      content: <AccountApprovals />,
      requiredPermission: {
        resource: 'accountRequests',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'organization-directory',
      tab: 'Organization Directory',
      content: <Organizations />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'manage-roles',
      tab: 'Manage Roles',
      content: <Roles />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    }
  ], [PERMISSION_LEVELS]);

  // Filter tabs based on permissions
  const accessibleTabs = useMemo(() => {
    if (!initialized || loading || !user) {
      return [];
    }
    return tabs.filter(tab => 
      hasPermission(
        tab.requiredPermission.resource,
        tab.requiredPermission.action,
        tab.requiredPermission.level
      )
    );
  }, [tabs, hasPermission, initialized, loading, user]);

  // Ensure a valid active tab is selected
  useEffect(() => {
    if (initialized && !loading && user && accessibleTabs.length > 0) {
      if (!accessibleTabs.some(tab => tab.key === activeTabKey)) {
        setActiveTabKey(accessibleTabs[0].key);
      }
    }
  }, [activeTabKey, initialized, loading, user, accessibleTabs]);

  // Loading state
  if (!initialized || loading || !user) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Spin size="large" tip="Loading User Access Management..." />
      </div>
    );
  }

  // No access state
  if (accessibleTabs.length === 0) {
    return (
      <div className="admin-page-container">
        <h1 className="page-title">User & Access Management</h1>
        <p className="page-description">You do not have permission to view any user access management tabs.</p>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <h1 className="page-title">User & Access Management</h1>
      <p className="page-description">
        Manage users, organizations, roles, and account approvals.
      </p>

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        className="admin-panel-tabs"
      >
        {accessibleTabs.map(tab => (
          <TabPane tab={tab.tab} key={tab.key}>
            {tab.content}
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default UserAccessPage;